import { useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import { type GroupMemberDTO, setLadderSeed } from "../api";

// Admin hand-seeds the ladder: drag members into top-to-bottom order, save.
// Existing matches (if any) replay on top of the new seed order.
export function SeedLadderModal({
  groupId,
  members,
  format,
  onClose,
}: {
  groupId: string;
  members: GroupMemberDTO[];
  format: "singles" | "doubles";
  onClose: (seeded: boolean) => void;
}) {
  const [ordered, setOrdered] = useState<GroupMemberDTO[]>(members);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PointerSensor (mouse + touch) only starts a drag after a small move, so a
  // tap/scroll on the grip doesn't trigger one. KeyboardSensor makes the grip
  // arrow-key reorderable for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrdered((items) => {
      const from = items.findIndex((m) => m.user_id === active.id);
      const to = items.findIndex((m) => m.user_id === over.id);
      if (from < 0 || to < 0) return items;
      return arrayMove(items, from, to);
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await setLadderSeed(groupId, format, ordered.map((m) => m.user_id));
      onClose(true);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={() => onClose(false)} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2">
        <div className="max-w-lg mx-auto px-4 pb-4" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.16em]">Seed ladder</div>
                <div className="text-bg/60 text-xs capitalize">{format} · strongest at top</div>
              </div>
              <button
                onClick={() => onClose(false)}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {error && (
                <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <p className="text-[11px] text-ink/45 mb-3">
                Drag players into the starting order. It's a guess — recorded results will move them from here.
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={ordered.map((m) => m.user_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ol className="flex flex-col gap-1.5 mb-4">
                    {ordered.map((m, i) => (
                      <SortableRow key={m.user_id} member={m} position={i + 1} />
                    ))}
                  </ol>
                </SortableContext>
              </DndContext>
              <button
                onClick={() => void save()}
                disabled={busy}
                className="w-full px-5 py-2.5 bg-accent text-ink font-display font-bold text-sm cs-tap disabled:opacity-40"
              >
                {busy ? "Saving…" : "Save order"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SortableRow({ member, position }: { member: GroupMemberDTO; position: number }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.user_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={
        "flex items-center gap-3 px-3 py-2 border rounded-md bg-bg " +
        (isDragging ? "relative z-10 border-ink/40 shadow-lg" : "border-ink/15")
      }
    >
      {/* Only the grip starts a drag — the rest of the row stays scrollable on touch. */}
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Drag ${member.name} to reorder`}
        className="touch-none cursor-grab active:cursor-grabbing text-ink/30 hover:text-ink/60 -ml-1 shrink-0"
      >
        <GripVertical size={18} />
      </button>
      <span className="w-6 text-right font-mono text-sm text-ink/45 tabular-nums shrink-0">
        {position}
      </span>
      <span className="w-7 h-7 rounded-full flex items-center justify-center font-display font-bold text-xs shrink-0 bg-bg-3 text-ink">
        {member.initials}
      </span>
      <span className="flex-1 truncate text-sm">{member.name}</span>
    </li>
  );
}
